import { Lock, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@roger/ui-components';

interface SaveReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'save' | 'copy' | 'edit';
  initialName: string;
  initialDescription: string;
  // Only shown when submit will CREATE a report (save-a-copy, or save when not
  // owner) — hidden in update-in-place saves and in the homepage 'edit' mode
  // (visibility changes there go through the dedicated menu actions instead).
  showVisibility?: boolean;
  onSave: (
    name: string,
    description: string,
    visibility?: 'private' | 'agency'
  ) => Promise<void>;
}

const SaveReportDialog = ({
  open,
  onOpenChange,
  mode,
  initialName,
  initialDescription,
  showVisibility = false,
  onSave,
}: SaveReportDialogProps) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  // Privacy-preserving default (D10): every reopen starts at "Only me".
  const [visibility, setVisibility] = useState<'private' | 'agency'>('private');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(
        mode === 'copy' && initialName ? `Copy of ${initialName}` : initialName
      );
      setDescription(initialDescription);
      setVisibility('private');
    }
  }, [open, mode, initialName, initialDescription]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(
        name.trim(),
        description.trim(),
        showVisibility ? visibility : undefined
      );
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit'
              ? 'Edit report'
              : mode === 'copy'
                ? 'Save a copy'
                : 'Save report'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Name this report and optionally describe it before saving.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <Label htmlFor='report-name'>Name</Label>
            <Input
              id='report-name'
              placeholder='Report name'
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='report-description'>Description (optional)</Label>
            <Textarea
              id='report-description'
              textareaSize='sm'
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          {showVisibility && (
            <div className='space-y-1.5'>
              <Label>Who can open this</Label>
              <div
                className='inline-flex rounded-lg border border-border/80 bg-muted/40 p-1 shadow-sm'
                role='group'
                aria-label='Who can open this'
              >
                <Button
                  type='button'
                  size='sm'
                  variant={visibility === 'private' ? 'default' : 'ghost'}
                  onClick={() => setVisibility('private')}
                >
                  <Lock className='mr-2 h-4 w-4' />
                  Only me
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant={visibility === 'agency' ? 'default' : 'ghost'}
                  onClick={() => setVisibility('agency')}
                >
                  <Users className='mr-2 h-4 w-4' />
                  My agency
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || name.trim().length === 0}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveReportDialog;
